<script setup>
import { computed, onMounted, ref } from 'vue'
import PollCard from './components/PollCard.vue'
import PollForm from './components/PollForm.vue'
import { ApiError, apiBaseUrl } from './api/client'
import { usePolls } from './composables/usePolls'

const { polls, loading, error, pendingPollIds, totalVotes, refresh, createPoll, vote, removePoll } =
  usePolls()

const keyword = ref('')
const sortBy = ref('new')
const submitting = ref(false)
const formErrors = ref([])

// 后端地址是否已配置。未配置时前端会请求同源 /api/v1，
// 线上必然失败，所以给出明确的引导而不是笼统的报错。
const backendConfigured = computed(() => apiBaseUrl().startsWith('http'))

// onMounted：组件挂载后再发请求，此时 DOM 已就绪。
onMounted(refresh)

const visiblePolls = computed(() => {
  const query = keyword.value.trim().toLowerCase()
  const filtered = polls.value.filter((poll) => {
    if (!query) return true
    return (
      poll.title.toLowerCase().includes(query) ||
      poll.options.some((option) => option.text.toLowerCase().includes(query))
    )
  })

  const tally = (poll) => poll.options.reduce((sum, option) => sum + option.votes, 0)
  return [...filtered].sort((a, b) =>
    sortBy.value === 'hot'
      ? tally(b) - tally(a)
      : new Date(b.createdAt) - new Date(a.createdAt)
  )
})

async function handleCreate({ title, options, reset }) {
  submitting.value = true
  formErrors.value = []
  try {
    await createPoll(title, options)
    reset()
    keyword.value = ''
    sortBy.value = 'new'
  } catch (err) {
    // 422 带字段级错误，回填到表单对应输入框；其他错误走全局提示。
    if (err instanceof ApiError && err.details.length > 0) {
      formErrors.value = err.details
    } else {
      error.value = err instanceof ApiError ? err.message : '创建失败'
    }
  } finally {
    submitting.value = false
  }
}

// 失败已在 store 里回滚并记录，这里只需吞掉异常避免未处理的 rejection。
async function handleVote({ pollId, optionId }) {
  try {
    await vote(pollId, optionId)
  } catch {
    /* 错误已反映在 error 状态上 */
  }
}

async function handleRemove(pollId) {
  try {
    await removePoll(pollId)
  } catch {
    /* 同上 */
  }
}
</script>

<template>
  <div class="page">
    <header class="hero">
      <h1>🗳️ 投票站</h1>
      <p>Vue 3 前端 + TypeScript 后端，票数实时共享给所有访问者。</p>
      <p class="stats">{{ polls.length }} 个投票 · 累计 {{ totalVotes }} 票</p>
    </header>

    <p v-if="!backendConfigured && error" class="banner" role="alert">
      后端 API 尚未配置。部署 Worker 后端后，把地址填入
      <code>public/config.json</code> 的 <code>apiBaseUrl</code> 并重新部署前端即可。
    </p>

    <p v-else-if="error" class="banner" role="alert">
      {{ error }}
      <button @click="refresh">重试</button>
    </p>

    <div class="layout">
      <section class="list">
        <div class="toolbar">
          <input v-model="keyword" placeholder="搜索投票…" aria-label="搜索投票" />
          <div class="sorts">
            <button :class="{ primary: sortBy === 'new' }" @click="sortBy = 'new'">最新</button>
            <button :class="{ primary: sortBy === 'hot' }" @click="sortBy = 'hot'">最热</button>
            <button :disabled="loading" @click="refresh">
              {{ loading ? '刷新中…' : '刷新' }}
            </button>
          </div>
        </div>

        <!-- 三种状态分别渲染：加载中 / 空 / 有数据 -->
        <div v-if="loading && polls.length === 0" class="skeletons">
          <div v-for="n in 2" :key="n" class="skeleton" />
        </div>

        <p v-else-if="visiblePolls.length === 0" class="empty">
          {{ keyword ? '没有匹配的投票，换个关键词试试。' : '还没有投票，在右侧新建第一个吧。' }}
        </p>

        <TransitionGroup v-else name="fade" tag="div" class="grid">
          <PollCard
            v-for="poll in visiblePolls"
            :key="poll.id"
            :poll="poll"
            :pending="pendingPollIds.has(poll.id)"
            @vote="handleVote"
            @remove="handleRemove"
          />
        </TransitionGroup>
      </section>

      <aside>
        <PollForm :submitting="submitting" :field-errors="formErrors" @create="handleCreate" />
      </aside>
    </div>

    <footer class="site-footer">
      前端在 Cloudflare Pages，API 由 Go 实现。
      <code>src/api/client.js</code> 是唯一的网络出口，<code>src/composables/usePolls.js</code> 管状态。
    </footer>
  </div>
</template>

<style scoped>
.page { max-width: 1080px; margin: 0 auto; padding: 32px 20px 64px; }

.hero h1 { margin: 0 0 6px; font-size: 30px; }
.hero p { margin: 0; color: var(--muted); }
.stats { margin-top: 6px !important; font-variant-numeric: tabular-nums; }

.banner {
  margin: 20px 0 0;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #5c2b2b;
  background: #2a1717;
  color: #ffb4ad;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.layout {
  margin-top: 28px;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr) 320px;
  align-items: start;
}

.toolbar { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.toolbar input { flex: 1 1 220px; }
.sorts { display: flex; gap: 8px; }

.grid, .skeletons { display: grid; gap: 16px; }
.empty { color: var(--muted); }

.skeleton {
  height: 176px;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: linear-gradient(100deg, #181b22 30%, #20242e 50%, #181b22 70%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite linear;
}

@keyframes shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(6px); }

.site-footer { margin-top: 40px; font-size: 13px; color: var(--muted); }
code { background: #12151b; border: 1px solid var(--border); border-radius: 5px; padding: 1px 5px; }

@media (max-width: 860px) {
  .layout { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton { animation: none; }
  .fill, .fade-enter-active, .fade-leave-active { transition: none; }
}
</style>
